// Homepage, search results and browse by system (EXP 01, EXP 02).

import { esc } from '../api';
import { HUB_NAME } from '../brand';
import type content from '../content/sample-content.json';
import type { SearchItem } from '../search';
import { formatWhen } from '../notifications';

type Content = typeof content;

export interface UpcomingChange {
  id: string;
  title: string;
  systems: string[];
  scheduledStart: string;
  timezone: string;
  state: string;
}

const KIND_LABEL: Record<SearchItem['kind'], string> = {
  tool: 'Tool',
  guide: 'Guide',
  training: 'Training',
  change: 'Change',
  page: 'Page',
};

export function renderResults(results: SearchItem[], query: string): string {
  if (!query.trim()) return '';
  if (results.length === 0) return `<p class="muted" role="status">No sample content matches “${esc(query)}”.</p>`;
  return `<ul class="results" aria-label="Search results">${results
    .map(
      (r) =>
        `<li><a href="${esc(r.href)}">${esc(r.title)}</a> <span class="tag">${KIND_LABEL[r.kind]}</span>${
          r.system ? ` <span class="muted">${esc(r.system)}</span>` : ''
        }</li>`,
    )
    .join('')}</ul>`;
}

export function renderHome(data: Content, upcoming: UpcomingChange[], query = '', results: SearchItem[] = []): string {
  const soon = upcoming.filter((c) => c.state !== 'cancelled').slice(0, 3);
  // No banner: the page opens on its content. The masthead shows the Hub's
  // name but is not a heading, so the page keeps one h1 for screen readers.
  // Search results, when there is a search, come first.
  const searchResults = query.trim()
    ? `
<section aria-labelledby="results-title">
  <h2 id="results-title">Search results for “${esc(query)}”</h2>
  <div id="results">${renderResults(results, query)}</div>
</section>`
    : '';
  return `
<h1 class="visually-hidden">${esc(HUB_NAME)}</h1>${searchResults}
<nav class="areas" aria-label="Main areas">
  ${data.areas
    .map((a) => `<a class="card" href="${esc(a.href)}"><h2>${esc(a.title)}</h2><p>${esc(a.summary)}</p></a>`)
    .join('')}
</nav>
<section aria-labelledby="upcoming-title">
  <h2 id="upcoming-title">Upcoming changes</h2>
  ${
    soon.length
      ? `<ul class="upcoming">${soon
          .map(
            (c) =>
              `<li><a href="#/calendar/${esc(c.id)}">${esc(c.title)}</a> <span class="muted">${esc(
                formatWhen(c.scheduledStart, c.timezone),
              )} · ${esc(c.systems.join(', '))}</span>${c.state === 'rescheduled' ? ' <span class="tag">Rescheduled</span>' : ''}</li>`,
          )
          .join('')}</ul>`
      : '<p class="muted">No upcoming changes.</p>'
  }
  <p><a href="#/calendar">See the full change calendar</a></p>
</section>
<section aria-labelledby="systems-title">
  <h2 id="systems-title">Browse by system</h2>
  <ul class="chips">${data.systems.map((s) => `<li><a href="#/system/${encodeURIComponent(s)}">${esc(s)}</a></li>`).join('')}</ul>
  <p><a href="#/tools">Tools directory</a></p>
</section>`;
}

export function renderSystem(system: string, items: SearchItem[]): string {
  return `
<h1>${esc(system)}</h1>
${items.length ? renderResults(items, system) : '<p class="muted">No sample content for this system yet.</p>'}
<p><a href="#/">Back to the Hub</a></p>`;
}
